/**
 * What a Detail command needs to run an outline bulk handler for its own record.
 *
 * The handlers write straight through the entity API and then patch the board row in place, which is
 * everything the board needs and nothing the mounted Detail needs — so a Detail run adds two things:
 * a patcher that does nothing when the surface is not inside a board, and a re-read that puts the
 * server's answer back into the surface model.
 */
import { ActivityEntityName, Entity } from '@jms/model';
import { EntityDataServiceRaw } from '@workbuddy/services/entities';
import { firstValueFrom } from 'rxjs';
import type { SurfaceContextLike } from '@workbuddy/ui/v2/surface.engine';
import type { OutlineRowPatcher } from './outline.bulk.schedule';

/** Stands in for the board store when a Detail is mounted outside a board (dialog, standalone route). */
export const NOOP_OUTLINE_ROW_PATCHER: OutlineRowPatcher = { patchDisplayedRow: () => undefined };

/**
 * Re-reads the record and replaces the surface's clean baseline with it. The handler bypassed the
 * surface to write, so without this the Detail would keep rendering the pre-command values and the
 * next patch would send them straight back. `toModel` rebuilds whatever view-only keys the schema
 * binds alongside the record, so a reload lands the same model shape the mount started with.
 */
export async function reloadSurfaceRecord<TModel extends Entity>(entityData: EntityDataServiceRaw, entity: ActivityEntityName, page: SurfaceContextLike<TModel>, id: string, toModel: (record: TModel) => Record<string, unknown> = record => ({ ...record } as Record<string, unknown>)): Promise<void> {

  const saved = (await firstValueFrom(entityData.getById<TModel>(entity, id)))?.item;
  if (saved) page.reset(toModel(saved));
}
